import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, Input,
  Output, EventEmitter, OnChanges, SimpleChanges
} from '@angular/core';

import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { SearchType } from '@search-bar/entities/search-type';
import { searchTypeToSearchBy } from '@shared/base-search-bar/mappers/search-by-and-type.mapper';
import { RadioButtonAlign } from '@design-system/components/organisms/radio-button/entities/radio-button.types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { calculatePassengerArrangement } from '@app/modules/shared/base-search-bar/helpers/search-engine.helper';
import { TypographyFontFamily, TypographyFontSize, TypographyColor
} from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-search-bar-passenger-selection',
  templateUrl: './search-bar-passenger-selection.component.html',
  styleUrls: ['./search-bar-passenger-selection.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SearchBarPassengerSelectionComponent implements OnChanges {

  @Input() searchType: SearchType;
  @Input() passengerArrangement: PassengerArrangement[];
  @Input() activeFlightClass: FlightClass;
  @Input() eventPath: EventTarget[];
  @Input() isMobile: boolean = false;
  @Input() seatsOnly: boolean = false;
  @Input() activeOption: string;

  @Output() emitArrangement: EventEmitter<PassengerArrangement> = new EventEmitter<PassengerArrangement>();
  @Output() emitFlightClassSelection: EventEmitter<FlightClass> = new EventEmitter<FlightClass>();
  @Output() emitApplySelection: EventEmitter<void> = new EventEmitter<void>();
  public flightClassTypes: FlightClass[] = [
    FlightClass.CHEAPER,
    FlightClass.PREMIUM,
    FlightClass.EXECUTIVE,
    FlightClass.FIRST_CLASS
  ];

  public showComponent: boolean = true;

  public maxPassengers: number = 9;
  public totalPassengers: number = 0;

  public readonly radioButtonAlign = RadioButtonAlign.Right;

  public readonly icons = Icon;

  public readonly typographyFontFamily = TypographyFontFamily;
  public readonly typographyFontSize = TypographyFontSize;
  public readonly typographyColor = TypographyColor;

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.eventPath) {
      const inputButton = changes.eventPath.currentValue.find(paths =>
        !!paths['classList'] && paths['classList'].length > 0
        && paths['classList'].contains('passengers-input'));
      if (!inputButton && this.showComponent) this.toggleInput();
    }
  }

  get isResultsPage(): boolean {
    return this.searchType === SearchType.FLIGHT_RESULTS || this.searchType === SearchType.BUSES_RESULTS ||
      this.searchType === SearchType.PACKAGES_RESULT;
  }

  get passengerLabel(): string {
    return this.isBus ? 'SEARCH_MODULE.PASSENGERS' : 'SEARCH_MODULE.PASSENGERS_CLASS';
  }

  get isBus(): boolean {
    return this.searchType === SearchType.BUSES_RESULTS || this.searchType === SearchType.HOME_BUSES;
  }

  get searchBy(): SearchBy {
    return searchTypeToSearchBy(this.searchType);
  }

  public passengerArrangementLabel(type: PassengerArrangement): string {
    return !this.seatsOnly ? `SEARCH_MODULE.PASSENGER_ARRANGEMENT.LABEL.${type.id}` : '';
  }

  public passengerArrangementTitle(type: PassengerArrangement): string {
    return !this.seatsOnly ?
      this.isBus ? 'SEARCH_MODULE.PASSENGER_ARRANGEMENT.SEATS' :
        `SEARCH_MODULE.PASSENGER_ARRANGEMENT.TYPE.${type.id}` : 'SEARCH_MODULE.PASSENGER_ARRANGEMENT.SEATS';
  }

  public toggleInput(): void {
    this.showComponent = !this.showComponent;
  }

  public isActiveClass(flightClass: FlightClass): boolean {
    return flightClass === this.activeFlightClass;
  }

  public selectFlightClass(flightClass: FlightClass): void {
    if (this.isMobile || this.isResultsPage) this.applySelection();
    this.emitFlightClassSelection.emit(flightClass);
  }

  public updatePassengerArrangement(quantity: number, type: PassengerArrangement): void {
    if (!this.seatsOnly) {
      const { updatedArrangement, adjustedQuantity } = calculatePassengerArrangement(
        quantity,
        type,
        this.passengerArrangement,
        this.maxPassengers
      );
      this.passengerArrangement = updatedArrangement;
      this.totalPassengers = this.passengerArrangement.reduce((sum, p) => sum + p.quantity, 0);
      this.emitArrangement.emit({ ...type, quantity: adjustedQuantity });
    }
  }

  public applySelection(): void {
    this.toggleInput();
    this.emitApplySelection.emit();
  }

  get iconByDevice(): Icon {
    return this.isMobile ? this.icons.PersonOutline : this.icons.ChevronFilledDown;
  }

}
